import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import Loader from 'app/components/atoms/Loader/Loader';

const THRESHOLD = 70;
const MAX_PULL = 110;

const Wrapper = styled.div`
    position: relative;
    height: 100%;
`;

const Indicator = styled.div.attrs(({ offset }) => ({
    style: { top: `${Math.max(offset - 20, -20)}px` },
}))`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 200;
    pointer-events: none;
`;

const findScrollContainer = (el, depth) => {
    if (!el || depth > 6) return null;
    for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i];
        const style = window.getComputedStyle(child);
        if (style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll') {
            return child;
        }
        const found = findScrollContainer(child, depth + 1);
        if (found) return found;
    }
    return null;
};

const getScrollTop = (containerRef, wrapperEl) => {
    if (containerRef && containerRef.current) return containerRef.current.scrollTop;
    const el = findScrollContainer(wrapperEl, 0);
    return el ? el.scrollTop : 0;
};

/**
 * Wraps scrollable content with pull-to-refresh gesture support on touch-capable devices.
 * Pass containerRef if the scroll container is a parent of this component
 * (e.g. when PullToRefresh is rendered inside the scroll container itself).
 */
const PullToRefresh = ({ onRefresh, containerRef, children }) => {
    const wrapperRef = useRef(null);
    const onRefreshRef = useRef(onRefresh);
    const [indicator, setIndicator] = useState({ offset: 0, refreshing: false });

    useEffect(() => {
        onRefreshRef.current = onRefresh;
    }, [onRefresh]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const s = { startY: 0, active: false, distance: 0, refreshing: false };

        const onStart = e => {
            if (s.refreshing) return;
            const scrollTop = getScrollTop(containerRef, wrapper);
            if (scrollTop === 0) {
                s.startY = e.touches[0].clientY;
                s.active = true;
            }
        };

        const onMove = e => {
            if (!s.active || s.refreshing) return;
            const delta = e.touches[0].clientY - s.startY;
            if (delta <= 0) {
                s.active = false;
                s.distance = 0;
                setIndicator({ offset: 0, refreshing: false });
                return;
            }
            e.preventDefault();
            s.distance = Math.min(delta * 0.5, MAX_PULL);
            setIndicator({ offset: s.distance, refreshing: false });
        };

        const onEnd = async () => {
            if (!s.active) return;
            s.active = false;
            const dist = s.distance;
            s.distance = 0;
            if (dist >= THRESHOLD && onRefreshRef.current) {
                s.refreshing = true;
                setIndicator({ offset: 44, refreshing: true });
                try {
                    await Promise.resolve(onRefreshRef.current());
                } finally {
                    s.refreshing = false;
                    setIndicator({ offset: 0, refreshing: false });
                }
            } else {
                setIndicator({ offset: 0, refreshing: false });
            }
        };

        wrapper.addEventListener('touchstart', onStart, { passive: true });
        wrapper.addEventListener('touchmove', onMove, { passive: false });
        wrapper.addEventListener('touchend', onEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('touchstart', onStart);
            wrapper.removeEventListener('touchmove', onMove);
            wrapper.removeEventListener('touchend', onEnd);
        };
    }, [containerRef]);

    const { offset, refreshing } = indicator;
    const visible = offset > 5 || refreshing;

    return (
        <Wrapper ref={wrapperRef}>
            {visible && (
                <Indicator offset={offset}>
                    <Loader size={36} />
                </Indicator>
            )}
            {children}
        </Wrapper>
    );
};

export default PullToRefresh;
